import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useViewport } from '../../hooks/useViewport';
import { AspectSentiment } from '../../types/analytics';

interface AspectBarChartProps {
  data: AspectSentiment[];
  height?: number;
}

const AspectBarChart: React.FC<AspectBarChartProps> = ({ data, height = 300 }) => {
  const { isMobile, isTablet } = useViewport();

  const chartData = data.map(({ aspect: name, avg_score: value }) => ({
    name: name
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '),
    // Truncate long names on mobile
    displayName: isMobile
      ? name.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ').substring(0, 10) + (name.length > 10 ? '...' : '')
      : name.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
    value,
  }));

  const getColor = (value: number): string => {
    if (value > 0.3) return '#10b981'; // green
    if (value < -0.3) return '#ef4444'; // red
    return '#f59e0b'; // yellow
  };

  // Responsive values
  const fontSize = isMobile ? 10 : 12;
  const responsiveHeight = isMobile ? Math.max(200, chartData.length * 35) : height;
  const leftMargin = isMobile ? 60 : isTablet ? 80 : 100;
  const margins = { top: 5, right: isMobile ? 10 : 30, left: leftMargin, bottom: 5 };

  return (
    <ResponsiveContainer width="100%" height={responsiveHeight}>
      <BarChart
        data={chartData}
        layout="vertical"
        margin={margins}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          type="number"
          domain={[-1, 1]}
          tick={{ fontSize, fontFamily: 'Open Sans', fill: '#767472' }}
          stroke="#e5e7eb"
        />
        <YAxis
          type="category"
          dataKey={isMobile ? 'displayName' : 'name'}
          tick={{ fontSize, fontFamily: 'Open Sans', fill: '#767472' }}
          stroke="#e5e7eb"
          width={leftMargin - 10}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontFamily: 'Open Sans',
            fontSize,
          }}
          formatter={(value: number) => [value.toFixed(3), 'Sentiment']}
          labelFormatter={(label) => {
            // Show full name in tooltip
            const item = chartData.find(d => d.displayName === label || d.name === label);
            return item?.name || label;
          }}
        />
        <Bar dataKey="value" radius={[0, 8, 8, 0]}>
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={getColor(entry.value)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default AspectBarChart;
